declare global {
  interface Window {
    zE: any;
  }
}

import { Epic } from 'redux-observable';
import { isActionOf } from 'typesafe-actions';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
} from 'rxjs/operators';
import { empty } from 'rxjs';

// Utils
import { jsonConvert } from '../util/general';
import {
  apiRequest,
  apiRequestError,
} from '../util/api';
import { getUserFilterAccommodationTypeConstraints, getUserFilterVehiclesConstraints } from '../util/filters';
import { errorLogger } from '../util/errors';
// Constants
import {
  API_METHOD_TYPE,
  API_URL,
} from '../const/api';
// Actions
import {
  adminUserActions,
  filterActions,
  generalActions,
  organizationActions,
  usersActions,
} from '../actions';
// Models
import {
  UserModel,
} from '../models';
// Interfaces
import { RootAction } from '../interfaces';

export const verifyUserEpic:Epic<RootAction, RootAction> = (action$) =>
  action$.pipe(
    filter(isActionOf(adminUserActions.verifyUserAsync.request)),
    switchMap((action) =>
      apiRequest(API_URL.WHOAMI).pipe(
        mergeMap((res:any) => {
          if (res.response.signup_complete) {
            const user:UserModel = jsonConvert.deserializeObject(res.response, UserModel);

            return [
              adminUserActions.setUserProfile(user),
              organizationActions.setOrganization(user.organization),
              generalActions.applyExtActionAsync.request({ callback: action.payload.onSuccess, param: user }),
              filterActions.setTripTransportationUserFilter(
                getUserFilterVehiclesConstraints(user)
              ),
              filterActions.setHotelAccommodationUserFilter({
                current: getUserFilterAccommodationTypeConstraints(user)
              }),
              filterActions.setTripSortingUserFilter({
                current: user.preference.transportationSorting,
              }),
              filterActions.setHotelSortingUserFilter({
                current: user.preference.hotelSorting,
              }),
              filterActions.setHotelRatingUserFilter({
                current: user.preference.hotelRatingFilterMin,
              }),
            ];
          } else {
            return [
              generalActions.applyExtActionAsync.request({ callback: action.payload.onError, param: null }),
            ];
          }
        }),
        catchError((err) => {
          return apiRequestError(action.payload.onError, 'VerifyUserError', err);
        })
      )
    )
  );

export const fetchOneTimeLoginTokenEpic: Epic<RootAction, RootAction> = (action$) =>
  action$.pipe(
    filter(isActionOf(adminUserActions.fetchOneTimeLoginTokenAsync.request)),
    switchMap((action) =>
      apiRequest(API_URL.LOGIN_TOKENS, API_METHOD_TYPE.POST).pipe(
        map((res) => {
          return generalActions.applyExtActionAsync.request({
            callback: action.payload.onSuccess,
            param: res.response.token,
          });
        }),
        catchError((err) => {
          return apiRequestError(action.payload.onError, 'GetUserLoginTokenError', err);
        }),
      ),
    ),
  );


const ZENDESK_LOGIN_MAX_RETRIES = 3;
const ZENDESK_LOGIN_RETRY_DELAY = 2000;
const ZENDESK_JWT_FETCH_MAX_RETRIES = 3;
const ZENDESK_TOKEN_REFRESH_BUFFER_MS = 60 * 1000;

const zendeskState = {
  tokenCache: null as string | null,
  refreshTimer: null as ReturnType<typeof setTimeout> | null,
  fetchAborted: false,
  loginGeneration: 0,
};

const scheduleRetry = (
  fn: () => void,
  retryCount: number,
  maxRetries: number,
  baseDelay: number,
  onExhausted: () => void,
) => {
  if (retryCount < maxRetries) {
    setTimeout(fn, baseDelay * (retryCount + 1));
  } else {
    onExhausted();
  }
};

const parseJwtExp = (token: string): number | null => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

const fetchAndCacheZendeskToken = (retryCount = 0, onToken?: (token: string) => void) => {
  if (zendeskState.fetchAborted) return;
  apiRequest(API_URL.ZENDESK_JWT).pipe(take(1)).subscribe({
    next: (res: any) => {
      if (zendeskState.fetchAborted) return;
      const token = res.response.zendesk_jwt;
      cacheZendeskToken(token);
      onToken?.(token);
    },
    error: (err) => {
      if (zendeskState.fetchAborted) return;
      scheduleRetry(
        () => fetchAndCacheZendeskToken(retryCount + 1, onToken),
        retryCount,
        ZENDESK_JWT_FETCH_MAX_RETRIES,
        ZENDESK_LOGIN_RETRY_DELAY,
        () => errorLogger('zendeskJwtRefreshError', err),
      );
    },
  });
};

const cacheZendeskToken = (token: string) => {
  zendeskState.tokenCache = token;
  const exp = parseJwtExp(token);
  if (!exp) return;
  const refreshIn = exp - Date.now() - ZENDESK_TOKEN_REFRESH_BUFFER_MS;
  if (refreshIn <= 0) return;
  if (zendeskState.refreshTimer) clearTimeout(zendeskState.refreshTimer);
  zendeskState.refreshTimer = setTimeout(() => fetchAndCacheZendeskToken(), refreshIn);
};

const clearZendeskSession = () => {
  zendeskState.tokenCache = null;
  zendeskState.fetchAborted = true;
  zendeskState.loginGeneration++;
  if (zendeskState.refreshTimer) {
    clearTimeout(zendeskState.refreshTimer);
    zendeskState.refreshTimer = null;
  }
};

const initZendeskSession = () => {
  zendeskState.fetchAborted = false;
  zendeskState.loginGeneration++;
};

const loginZendeskUser = (initialToken: string, retryCount = 0) => {
  if (!window?.zE) return;
  if (retryCount === 0) initZendeskSession();
  const generation = zendeskState.loginGeneration;
  let isFirstCallback = retryCount === 0;
  window.zE(
    'messenger',
    'loginUser',
    (callback) => {
      if (generation !== zendeskState.loginGeneration) return;
      if (isFirstCallback) {
        isFirstCallback = false;
        cacheZendeskToken(initialToken);
        callback(initialToken);
        return;
      }
      if (zendeskState.tokenCache) {
        callback(zendeskState.tokenCache);
        fetchAndCacheZendeskToken();
      } else {
        fetchAndCacheZendeskToken(0, callback);
      }
    },
    (error) => {
      if (!error) return;
      if (generation !== zendeskState.loginGeneration) return;
      scheduleRetry(
        () => loginZendeskUser(initialToken, retryCount + 1),
        retryCount,
        ZENDESK_LOGIN_MAX_RETRIES,
        ZENDESK_LOGIN_RETRY_DELAY,
        () => errorLogger('zendeskLoginError', error),
      );
    },
  );
};

export const addUserIdentityToZendeskEpic: Epic<RootAction, RootAction> = (action$) =>
  action$.pipe(
    filter(isActionOf(adminUserActions.zendeskWidgetLoaded)),
    switchMap((action) => {
      try {
        if (window?.zE) {
          const { userLocale, zendeskToken } = action.payload;
          window.zE('messenger:set', 'locale', userLocale === 'en-GB' ? 'en-US' : userLocale);
          loginZendeskUser(zendeskToken);
        }
      } catch (error) {
        errorLogger('addUserIdentityToZendeskError', error);
      }

      return empty();
    }),
  );

export const logoutUserFromZendeskEpic: Epic<RootAction, RootAction> = (action$) =>
  action$.pipe(
    filter(isActionOf(usersActions.doLogoutAsync.request)),
    switchMap(() => {
      try {
        window?.zE?.('messenger', 'logoutUser');
        clearZendeskSession();
        return empty();
      } catch (error) {
        errorLogger('logoutUserFromZendeskError', error);
        return empty();
      }
    }),
  );
